import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Save, Percent, Settings as SettingsIcon } from "lucide-react";

export default function AdminSettings() {
  const [opPct, setOpPct] = useState("");
  const [initialPct, setInitialPct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/admin/settings");
      const pct = r.data?.workshop_operational_pct ?? 35;
      setOpPct(String(pct));
      setInitialPct(pct);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function save() {
    const n = Number(opPct);
    if (Number.isNaN(n) || n < 0 || n > 100) {
      toast.error("Ingresá un porcentaje entre 0 y 100");
      return;
    }
    setSaving(true);
    try {
      const r = await api.patch("/admin/settings", { workshop_operational_pct: n });
      const pct = r.data?.workshop_operational_pct ?? n;
      setInitialPct(pct);
      setOpPct(String(pct));
      toast.success("Configuración actualizada");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  const preview = (() => {
    const n = Number(opPct);
    if (!isFinite(n) || n < 0 || n > 100) return null;
    const op = (100 * n) / 100;
    const base = 100 - op;
    return { op, base };
  })();

  const dirty = String(initialPct) !== String(opPct);

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-6" data-testid="admin-settings-page">
      <div className="flex items-center gap-2">
        <SettingsIcon className="text-slate-700" size={22} />
        <h1 className="text-xl font-heading font-bold text-slate-900">Configuración del taller</h1>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
        <div>
          <h2 className="font-heading font-bold text-slate-900 text-base">% operativo del taller</h2>
          <p className="text-xs text-slate-600 mt-1">
            Porcentaje imputado al taller sobre el <b>precio final</b> cuando una reparación
            <b> no tiene repuestos</b>. Si la reparación usa repuestos, este porcentaje no se aplica
            (la base repartible se calcula directamente como precio − repuestos).
          </p>
        </div>

        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-slate-500">Porcentaje operativo</span>
          <div className="mt-1 flex items-center gap-2">
            <input
              type="number"
              min={0}
              max={100}
              step={0.5}
              value={opPct}
              onChange={(e) => setOpPct(e.target.value)}
              disabled={loading}
              className="input-base max-w-[120px] text-right font-mono text-base"
              data-testid="settings-op-pct-input"
            />
            <Percent className="text-slate-400" size={16} />
          </div>
        </label>

        {preview && (
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs text-slate-700" data-testid="settings-op-pct-preview">
            <div className="font-mono uppercase tracking-wider text-slate-500 mb-1">Vista previa</div>
            Sobre un precio final de <b>$100</b> sin repuestos:
            <ul className="mt-1 list-disc list-inside text-slate-800">
              <li>Costo operativo del taller: <b>${preview.op.toFixed(2)}</b></li>
              <li>Base repartible: <b>${preview.base.toFixed(2)}</b></li>
            </ul>
            <div className="mt-1 text-[11px] text-slate-500">
              Luego, de esa base se descuenta el porcentaje individual configurado por técnico.
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={save}
            disabled={saving || loading || !dirty}
            data-testid="settings-save-btn"
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 text-white px-4 py-2 text-sm font-heading font-bold disabled:opacity-50 hover:bg-slate-700 transition-colors"
          >
            <Save size={14} /> {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </section>

      <div className="text-[11px] text-slate-500">
        Los cambios acá <b>no modifican</b> reparaciones ya finalizadas: cada orden conserva un
        snapshot del porcentaje aplicado al momento de finalizar.
      </div>
    </div>
  );
}
